import { useEffect, useRef, useState } from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import FormControl from '@mui/joy/FormControl'
import FormHelperText from '@mui/joy/FormHelperText'
import FormLabel from '@mui/joy/FormLabel'
import IconButton from '@mui/joy/IconButton'
import Input from '@mui/joy/Input'
import Sheet from '@mui/joy/Sheet'
import Stack from '@mui/joy/Stack'
import Typography from '@mui/joy/Typography'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
    TUTORIAL_DURATION_MAX_SECONDS,
    TUTORIAL_TITLE_MAX,
    TUTORIALS_MAX_ITEMS,
    parseYouTubeVideoId,
    youTubeThumbnailUrl,
    type TutorialVideo,
} from '@numeo/types'

/** An item as the editor holds it: fields fill in one at a time, so any may be missing. */
export type TutorialDraft = Partial<TutorialVideo> & { id: string }

/** The draft items stored on a tutorials section; anything that is not an object with an id is dropped. */
export function tutorialDraftsOf(props: Record<string, unknown> | undefined): TutorialDraft[] {
    const raw = props?.items
    if (!Array.isArray(raw)) return []
    return raw.filter((i): i is TutorialDraft => typeof i === 'object' && i !== null && typeof (i as { id?: unknown }).id === 'string')
}

/** What still blocks publishing, in words an operator can act on; empty when the list is ready. */
export function tutorialsProblem(items: TutorialDraft[], badDurationIds: ReadonlySet<string>): string {
    if (!items.length) return 'Tutorials needs at least one video.'
    const missing = items
        .map((item, i) => {
            const gaps = [
                !item.title?.trim() && 'a title',
                !parseYouTubeVideoId(item.videoUrl ?? '') && 'a YouTube link',
                badDurationIds.has(item.id) && 'a valid duration',
            ].filter(Boolean)
            return gaps.length ? `video ${i + 1} needs ${gaps.join(', ')}` : ''
        })
        .filter(Boolean)
    return missing.length ? `Tutorials: ${missing.join('; ')}.` : ''
}

const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? `tut-${crypto.randomUUID()}` : `tut-${Date.now()}-${Math.round(Math.random() * 1e6)}`)

const pad2 = (n: number) => String(n).padStart(2, '0')

const formatDuration = (seconds: number | undefined): string => {
    if (!seconds) return ''
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    return h ? `${h}:${pad2(m)}:${pad2(seconds % 60)}` : `${m}:${pad2(seconds % 60)}`
}

/** "184", "3:04" or "1:05:00" → seconds; empty → undefined; anything else (e.g. "3:4") → null. */
function parseDuration(text: string): number | undefined | null {
    const value = text.trim()
    if (!value) return undefined
    const match = /^(?:(?:(\d+):)?(\d+):(\d{2})|(\d+))$/.exec(value)
    if (!match) return null
    const [, h, m, s, plain] = match
    if (plain) return withinCap(Number(plain))
    if (Number(s) > 59 || (h && Number(m) > 59)) return null
    return withinCap(Number(h ?? 0) * 3600 + Number(m) * 60 + Number(s))
}

const withinCap = (total: number) => (total >= 1 && total <= TUTORIAL_DURATION_MAX_SECONDS ? total : null)

interface DurationFieldProps {
    seconds: number | undefined
    onChange: (seconds: number | undefined) => void
    onValidity: (valid: boolean) => void
}

/** Typed as m:ss or h:mm:ss; saved as whole seconds once it reads as a length. Unreadable text is reported so Publish waits for it. */
function DurationField({ seconds, onChange, onValidity }: DurationFieldProps) {
    const [text, setText] = useState(() => formatDuration(seconds))
    const parsed = parseDuration(text)
    const valid = parsed !== null

    useEffect(() => {
        setText((current) => (parseDuration(current) === seconds ? current : formatDuration(seconds)))
    }, [seconds])

    const reportValidity = useRef(onValidity)
    reportValidity.current = onValidity
    useEffect(() => {
        const report = reportValidity.current
        report(valid)
        return () => report(true)
    }, [valid])

    return (
        <FormControl size="sm" error={parsed === null}>
            <FormLabel>Duration (optional)</FormLabel>
            <Input
                value={text}
                placeholder="3:04"
                onChange={(e) => {
                    setText(e.target.value)
                    const next = parseDuration(e.target.value)
                    if (next !== null) onChange(next)
                }}
                onBlur={() => parsed !== null && setText(formatDuration(parsed))}
                sx={{ width: 120 }}
            />
            <FormHelperText>{valid ? 'Shown in See all' : 'Use m:ss or h:mm:ss, e.g. 3:04'}</FormHelperText>
        </FormControl>
    )
}

interface Props {
    items: TutorialDraft[]
    onUpdate: (update: (items: TutorialDraft[]) => TutorialDraft[]) => void
    onDurationValidity: (id: string, valid: boolean) => void
}

/** The video list of the Home "Tutorials" card: YouTube link, title and optional duration per video, in display order. */
export function TutorialsEditor({ items, onUpdate, onDurationValidity }: Props) {
    const patch = (id: string, fields: Partial<TutorialVideo>) => onUpdate((prev) => prev.map((item) => (item.id === id ? { ...item, ...fields } : item)))
    const move = (index: number, dir: -1 | 1) =>
        onUpdate((prev) => {
            const target = index + dir
            if (target < 0 || target >= prev.length) return prev
            const next = [...prev]
            ;[next[index], next[target]] = [next[target], next[index]]
            return next
        })

    return (
        <Stack spacing={1} sx={{ mt: 1 }}>
            <Typography level="body-xs" color="neutral">
                The thumbnail is the video&apos;s own YouTube thumbnail. The title is shown under it, up to two lines.
            </Typography>
            {items.map((item, i) => {
                const videoId = item.videoUrl ? parseYouTubeVideoId(item.videoUrl) : null
                return (
                    <Sheet key={item.id} variant="soft" sx={{ p: 1, borderRadius: 8, display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                        <Box
                            sx={{
                                width: 112,
                                aspectRatio: '16 / 9',
                                flexShrink: 0,
                                borderRadius: 6,
                                bgcolor: 'neutral.softBg',
                                backgroundImage: videoId ? `url(${youTubeThumbnailUrl(videoId)})` : undefined,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}
                        />
                        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                            <FormControl size="sm" error={!!item.videoUrl && !videoId}>
                                <FormLabel>YouTube link</FormLabel>
                                <Input
                                    value={item.videoUrl ?? ''}
                                    placeholder="https://youtu.be/…"
                                    onChange={(e) => patch(item.id, { videoUrl: e.target.value.trim() })}
                                />
                                <FormHelperText>
                                    {!item.videoUrl
                                        ? 'watch?v=, youtu.be, shorts or embed links'
                                        : videoId
                                          ? `Video id ${videoId}`
                                          : 'Not a YouTube video link'}
                                </FormHelperText>
                            </FormControl>
                            <FormControl size="sm">
                                <FormLabel>Title</FormLabel>
                                <Input
                                    value={item.title ?? ''}
                                    placeholder="Start Here: A Tour of the Numeo App Home Screen"
                                    slotProps={{ input: { maxLength: TUTORIAL_TITLE_MAX } }}
                                    onChange={(e) => patch(item.id, { title: e.target.value })}
                                />
                            </FormControl>
                            <DurationField
                                seconds={item.durationSeconds}
                                onChange={(durationSeconds) => patch(item.id, { durationSeconds })}
                                onValidity={(valid) => onDurationValidity(item.id, valid)}
                            />
                        </Stack>
                        <Stack sx={{ alignItems: 'center' }}>
                            <IconButton size="sm" variant="plain" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move video up">
                                <KeyboardArrowUp />
                            </IconButton>
                            <IconButton size="sm" variant="plain" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Move video down">
                                <KeyboardArrowDown />
                            </IconButton>
                            <IconButton
                                size="sm"
                                variant="plain"
                                color="danger"
                                onClick={() => onUpdate((prev) => prev.filter((x) => x.id !== item.id))}
                                aria-label="Remove video"
                            >
                                <DeleteOutlineIcon />
                            </IconButton>
                        </Stack>
                    </Sheet>
                )
            })}
            <Button
                size="sm"
                variant="outlined"
                disabled={items.length >= TUTORIALS_MAX_ITEMS}
                onClick={() => onUpdate((prev) => [...prev, { id: newId() }])}
                sx={{ alignSelf: 'flex-start' }}
            >
                + Add video{items.length >= TUTORIALS_MAX_ITEMS ? ` (max ${TUTORIALS_MAX_ITEMS})` : ''}
            </Button>
        </Stack>
    )
}
